import { Box, Card, Skeleton, Text } from '@timelyapp/react';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { GetIntegrationsApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { getShouldShowNewMondayIntegration } from '~redux/selectors/accounts';
import { getIsUserInExperimentalFeatureGroup } from '~redux/selectors/user';
import { useIntegrationModalManager } from '../hooks/useIntegrationModalManager';
import { IntegrationCard } from './Integrations.Card';

type Integration = GetIntegrationsApiResponse['data'][number];

// Skeleton component that mimics the IntegrationCard structure
const IntegrationCardSkeleton: React.FC = () => {
  return (
    <Card.Container flexDirection="row" gap="medium" alignItems="flex-start">
      <Skeleton.Frame size="xsmall" shape="square" />
      <Box.Column flexGrow={1}>
        <Skeleton.Text size="medium" width={200} />
        <Skeleton.Text size="xsmall" truncateLastLine />
      </Box.Column>
    </Card.Container>
  );
};

export const IntegrationsListAsCards: React.FC = () => {
  const { workspace, personalSpace } = useSelector(getContextualTicAccounts);

  const { setConfig, active, canManageWorkspace } = useIntegrationModalManager();

  const isExperimentalUser = useSelector(getIsUserInExperimentalFeatureGroup);
  const showNewMonday = useSelector(getShouldShowNewMondayIntegration);
  const accountId = active.workspaceAccountId || workspace?.id || personalSpace?.id!;

  const { data, isLoading } = ticApi.useGetIntegrationsQuery(
    { accountId },
    { skip: !accountId, refetchOnMountOrArgChange: true, refetchOnReconnect: true }
  );

  const integrations = useMemo(() => {
    return (data?.data || []).filter(integration => {
      if (integration.id === 'monday') return showNewMonday;
      return isExperimentalUser || !integration.experimental; // Hide experimental integrations for non-experimental users
    });
  }, [data, isExperimentalUser, showNewMonday]);

  return (
    <Box.Column gap="large" marginTop={'large'}>
      {isLoading ? (
        <Box.Grid gridTemplateColumns="1fr 1fr" gap="large">
          <IntegrationCardSkeleton />
          <IntegrationCardSkeleton />
        </Box.Grid>
      ) : (
        <Box.Grid gridTemplateColumns="1fr 1fr" gap="large">
          {!integrations?.length && <Text.Paragraph>No integrations available</Text.Paragraph>}
          {integrations?.map((integration: Integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              selected={active.toolId === integration.id}
              canManageWorkspace={canManageWorkspace}
              onConfigurationChange={setConfig}
            />
          ))}
        </Box.Grid>
      )}
    </Box.Column>
  );
};
